import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { venuesApi, VENUE_TYPES } from "../../lib/events";
import { toast } from "sonner";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const empty = {
  name: "", address: "", suburb: "", state: "NSW", postcode: "",
  country: "Australia", timezone: "Australia/Sydney", venue_type: "hall",
  capacity: 100, map_url: "", parking_info: "", accessibility_info: "",
  public_notes: "", internal_notes: "",
};

export default function VenueForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEdit) return;
    venuesApi.get(id)
      .then((v) => setForm({ ...empty, ...v, capacity: v.capacity }))
      .catch(() => toast.error("Could not load venue."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity, 10) || 0 };
      const saved = isEdit ? await venuesApi.update(id, payload) : await venuesApi.create(payload);
      toast.success(isEdit ? "Venue updated" : "Venue created");
      navigate(`/dashboard/venues/${saved.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save venue.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10"><Loader2 className="animate-spin" size={18} /></div>;

  return (
    <section className="p-6 sm:p-10" data-testid="venue-form-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/dashboard/venues" className="gt-btn-ghost text-xs mb-6 inline-flex" data-testid="venue-form-back">
          <ArrowLeft size={13} /> Back to venues
        </Link>

        <div className="gt-label mb-2">Venue</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter mb-8">
          {isEdit ? "Edit venue" : "New venue"}
        </h2>

        <form onSubmit={submit} className="flex flex-col gap-5 bg-white border border-[var(--gt-line)] rounded-sm p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="gt-form-label">Venue name <span className="text-[var(--gt-accent)]">*</span></label>
              <input required className="gt-input" value={form.name} onChange={upd("name")} data-testid="venue-form-name" />
            </div>
            <div>
              <label className="gt-form-label">Venue type</label>
              <select className="gt-input" value={form.venue_type || "hall"} onChange={upd("venue_type")} data-testid="venue-form-type">
                {VENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="gt-form-label">Total capacity <span className="text-[var(--gt-accent)]">*</span></label>
              <input required type="number" min="1" className="gt-input" value={form.capacity} onChange={upd("capacity")} data-testid="venue-form-capacity" />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Street address</label>
              <input className="gt-input" value={form.address || ""} onChange={upd("address")} data-testid="venue-form-address" />
            </div>
            <div>
              <label className="gt-form-label">Suburb</label>
              <input className="gt-input" value={form.suburb || ""} onChange={upd("suburb")} data-testid="venue-form-suburb" />
            </div>
            <div>
              <label className="gt-form-label">State</label>
              <select className="gt-input" value={form.state || "NSW"} onChange={upd("state")} data-testid="venue-form-state">
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="gt-form-label">Postcode</label>
              <input className="gt-input" value={form.postcode || ""} onChange={upd("postcode")} />
            </div>
            <div>
              <label className="gt-form-label">Timezone</label>
              <input className="gt-input" value={form.timezone || ""} onChange={upd("timezone")} />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Google Maps URL</label>
              <input className="gt-input" value={form.map_url || ""} onChange={upd("map_url")} placeholder="https://maps.google.com/…" />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Parking info</label>
              <textarea className="gt-input" rows={2} value={form.parking_info || ""} onChange={upd("parking_info")} />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Accessibility info</label>
              <textarea className="gt-input" rows={2} value={form.accessibility_info || ""} onChange={upd("accessibility_info")} />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Public notes <span className="text-[var(--gt-ink-3)] text-xs">(shown on public event page)</span></label>
              <textarea className="gt-input" rows={2} value={form.public_notes || ""} onChange={upd("public_notes")} />
            </div>
            <div className="sm:col-span-2">
              <label className="gt-form-label">Internal notes <span className="text-[var(--gt-ink-3)] text-xs">(organiser only)</span></label>
              <textarea className="gt-input" rows={2} value={form.internal_notes || ""} onChange={upd("internal_notes")} />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="gt-btn-primary" data-testid="venue-form-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {isEdit ? "Save" : "Create venue"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
